// Hooks
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SetTargetPayload } from 'src/redux/slices/analysis/types'
import { updateProject } from 'src/redux/slices/project/endpoints'
import { SurveyCityTargetFields } from 'src/redux/slices/project/types'
import { TextFieldOnChangeEvent } from 'src/types/event-handlers'

// MUI
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

///// Umiverse Imports
// Redux
import { setTarget } from '../../../redux/slices/analysis/reducers'
import {
  selectAnalysisIsFromCloud,
  selectTargetLong,
  selectTargetNear
} from '../../../redux/slices/analysis/selectors'
import AnalysisDropdownSelect, { AnalysisDropdown } from './AnalysisDropdownSelect'
// Components
import { DividerWrapper, Header } from './SettingLayout'

type PayloadWithoutValue = Omit<SetTargetPayload['payload'], 'value'>
const payloadToKey = (payload: PayloadWithoutValue) => {
  // TODO: the database should have a schema which better reflects redux
  const param = payload.parameter === 'value' ? 'percent' : 'year'
  const range = payload.target === 'near' ? 'near' : 'far'
  const key = `city_${range}_${param}` as SurveyCityTargetFields
  return key
}
export default () => {
  const dispatch = useDispatch()
  // TODO: abstract this
  const fromCloud = useSelector(selectAnalysisIsFromCloud)
  const near = useSelector(selectTargetNear)
  const long = useSelector(selectTargetLong)
  const [trigger, { data, isLoading }] = updateProject.useMutation({
    fixedCacheKey: `update-project-targets`,
  })
  const handleChange = useCallback(
    (payload: PayloadWithoutValue, e: TextFieldOnChangeEvent) => {
      const value = Number(e.target.value)
      const args = {
        ...payload,
        value,
      }
      dispatch(setTarget(args))
      if (fromCloud) {
        const projectKey = payloadToKey(payload)
        trigger({ [projectKey]: value })
      }
    },
    [dispatch, trigger, fromCloud],
  )

  return (
    <Card variant='outlined'>
      <CardContent>
        <Header name='Goals' />
        <Grid container spacing={2} justifyContent='center'>
          <Grid item xs={12}>
            <AnalysisDropdownSelect dropdown={AnalysisDropdown.TargetMetric} />
          </Grid>

          <Grid item container direction='column' alignItems='center' spacing={1} xs={6}>
            <Grid item container alignItems='center' spacing={2}>
              <DividerWrapper>
                <Typography variant='overline'>Short-term</Typography>
              </DividerWrapper>
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                onChange={(e) => handleChange({ parameter: 'year', target: 'near' }, e)}
                label='Target'
                value={near.year}
                type='number'
                InputProps={{
                  inputProps: {
                    min: 2020,
                    max: 2100,
                    step: 1,
                  },
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                onChange={(e) => handleChange({ parameter: 'value', target: 'near' }, e)}
                label='% compared to baseline'
                value={near.value}
                // helperText="%"
                type='number'
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: 100,
                    step: 1,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item container direction='column' alignItems='center' spacing={1} xs={6}>
            <Grid item container alignItems='center' spacing={2}>
              <DividerWrapper>
                <Typography variant='overline'>Long-term</Typography>
              </DividerWrapper>
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                onChange={(e) => handleChange({ parameter: 'year', target: 'long' }, e)}
                label='Target'
                value={long.year}
                type='number'
                InputProps={{
                  inputProps: {
                    min: 2020,
                    max: 2100,
                    step: 1,
                  },
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                onChange={(e) => handleChange({ parameter: 'value', target: 'long' }, e)}
                label='% compared to baseline'
                value={long.value}
                // helperText="Units"
                type='number'
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: 100,
                    step: 1,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
